import React, {useMemo, useState} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Markdown from 'react-markdown';
import type {Components} from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CircularProgress from '@mui/material/CircularProgress';
import BarChartIcon from '@mui/icons-material/BarChart';
import DownloadIcon from '@mui/icons-material/Download';
import TableRowsIcon from '@mui/icons-material/TableRows';
import {InvestigationDTO, StepDTO} from '../../../@types/agent-investigation';
import StepChart, {chartFor} from './StepChart';
import {downloadCsvFile, rowsToCsv} from '../../../utils/export/csv';

/** How many rows of a table are shown before the reader has to ask for the rest. */
const ROWS_SHOWN = 8;

export interface AgentInvestigationProps<M = Record<string, unknown>, D = unknown> {
    /** The investigation to show, filling in as the agent works. */
    investigation: InvestigationDTO<M, D>;
    /** Something a screen offers on a step, such as putting its query onto a register. */
    renderStepAction?: (step: StepDTO<D>) => React.ReactNode;
    /** Something a screen shows under a finished answer, such as proposals to accept. */
    renderAnswerExtras?: (investigation: InvestigationDTO<M, D>) => React.ReactNode;
    /** Rendered instead of the answer's markdown when a screen wants its own presentation. */
    renderAnswer?: (answer: string) => React.ReactNode;
    /**
     * What a link inside the answer does, given its target and its text. A screen passes this to act
     * on the agent's own references, such as navigating a canvas to a node.
     *
     * <p>Answers link to things inside the application, not to the web, so such a link is never
     * followed: following one reloads the single-page application and loses the conversation. Without
     * a handler the link is rendered as plain text, which is still readable, rather than as something
     * that looks clickable and is not.</p>
     */
    onAnswerLink?: (href: string, text: string) => void;
}

/** The words inside a link, for a handler that has only a name to go on. */
const textOf = (children: React.ReactNode): string =>
    React.Children.toArray(children)
        .map((child: React.ReactNode): string => (typeof child === 'string' ? child : ''))
        .join('')
        .trim();

/**
 * One question being researched: what the agent is doing, what each query returned, and the answer.
 *
 * <p>The workings are the point rather than decoration. A figure quoted in a meeting has to be
 * traceable to the query that produced it, so every step shows its reason, its arguments and its
 * result, and any result worth more than one row can be taken away as a file.</p>
 *
 * <p>Lifted from the recommendation agent's component of the same name on issue-1259-components,
 * with the register-specific "show in register" action generalised into {@code renderStepAction}.
 * At merge that copy becomes a thin wrapper over this one.</p>
 */
export function AgentInvestigation<M = Record<string, unknown>, D = unknown>({
    investigation,
    renderStepAction,
    renderAnswerExtras,
    renderAnswer,
    onAnswerLink,
}: AgentInvestigationProps<M, D>): React.ReactElement {
    const {steps, done, answer, error} = investigation;

    const markdownComponents = useMemo((): Components => ({
        // An anchor with no href: unfollowable, so a click cannot reload the application, but still
        // ordinary text that selects and copies with the sentence around it. A button would not:
        // browsers leave form controls out of a copied selection, so every node the answer named
        // would vanish from anything the reader pasted.
        a: ({href, children}): React.ReactElement => {
            if (!onAnswerLink) {
                return <>{children}</>;
            }
            const follow = (): void => onAnswerLink(href ?? '', textOf(children));
            return (
                <Link
                    role="link"
                    tabIndex={0}
                    underline="hover"
                    sx={{cursor: 'pointer'}}
                    onClick={follow}
                    onKeyDown={(event: React.KeyboardEvent): void => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            follow();
                        }
                    }}
                >
                    {children}
                </Link>
            );
        },
    }), [onAnswerLink]);

    return (
        <Stack spacing={1}>
            {steps.map((step: StepDTO<D>, index: number) => (
                <Step
                    key={`${step.tool}-${index}`}
                    step={step}
                    ordinal={index + 1}
                    action={renderStepAction ? renderStepAction(step) : null}
                />
            ))}

            {!done && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{px: 0.5, py: 0.75}}>
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">
                        {steps.length === 0 ? 'Working out what to look at' : 'Still working'}
                    </Typography>
                </Stack>
            )}

            {done && error && (
                <Typography variant="body2" color="error">{error}</Typography>
            )}

            {done && !error && answer && (
                renderAnswer ? renderAnswer(answer) : (
                    <Box sx={{
                        '& p': {typography: 'body2', mt: 0, mb: 1},
                        '& ul, & ol': {typography: 'body2', pl: 2.5, mt: 0, mb: 1},
                        '& li': {mb: 0.25},
                        '& strong': {fontWeight: 600},
                        '& p:last-child': {mb: 0},
                    }}>
                        <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{answer}</Markdown>
                    </Box>
                )
            )}

            {done && !error && renderAnswerExtras && renderAnswerExtras(investigation)}
        </Stack>
    );
}

interface StepProps<D> {
    step: StepDTO<D>;
    ordinal: number;
    action: React.ReactNode;
}

/** One query: why it was run, what it asked for, what came back, and the result to take away. */
function Step<D>({step, ordinal, action}: StepProps<D>): React.ReactElement {
    const table = step.table;
    const [showAllRows, setShowAllRows] = useState(false);
    // Opens on the chart when the agent judged one was the way to show this, so a chart that was
    // asked for is not one click away from being seen.
    const [asChart, setAsChart] = useState(Boolean(table?.chart));
    const chart = useMemo(() => (table ? chartFor(table) : null), [table]);
    const rows = useMemo(
        () => (table && !showAllRows ? table.rows.slice(0, ROWS_SHOWN) : table?.rows ?? []),
        [table, showAllRows],
    );
    const hidden = table ? table.rows.length - rows.length : 0;

    const download = (): void => {
        if (!table) {
            return;
        }
        downloadCsvFile(`${step.tool}.csv`, rowsToCsv(table.columns, table.rows));
    };

    return (
        <Paper variant="outlined" sx={{p: 1.25, backgroundColor: 'action.hover'}}>
            <Stack spacing={0.75}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Chip label={`Step ${ordinal}`} size="small" sx={{flexShrink: 0}} />
                    <Box sx={{flex: 1, minWidth: 0}}>
                        {step.why && (
                            <Typography variant="body2">{step.why}</Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" component="div">
                            {step.tool}
                            {step.arguments ? `: ${step.arguments}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div">
                            {step.summary}
                        </Typography>
                    </Box>
                </Stack>

                {table && asChart && chart && (
                    <StepChart data={chart} title={step.summary} />
                )}

                {table && !asChart && (
                    <Box>
                        <Table size="small" sx={{'& td, & th': {py: 0.25, px: 0.75}}}>
                            <TableHead>
                                <TableRow>
                                    {table.columns.map((column: string) => (
                                        <TableCell key={column}>
                                            <Typography variant="caption" fontWeight={600}>{column}</Typography>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row: string[], rowIndex: number) => (
                                    <TableRow key={`row-${rowIndex}`}>
                                        {row.map((cell: string, cellIndex: number) => (
                                            <TableCell key={`cell-${rowIndex}-${cellIndex}`}>
                                                <Typography variant="caption">{cell}</Typography>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}

                {table && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{mt: 0.5}}>
                        {!asChart && hidden > 0 && (
                            <Link component="button" variant="caption" onClick={(): void => setShowAllRows(true)}>
                                {`Show ${hidden} more`}
                            </Link>
                        )}
                        {!asChart && showAllRows && table.rows.length > ROWS_SHOWN && (
                            <Link component="button" variant="caption" onClick={(): void => setShowAllRows(false)}>
                                Show fewer
                            </Link>
                        )}
                        <Box sx={{flex: 1}} />
                        {chart && (
                            <Tooltip title={asChart ? 'Show as a table' : 'Show as a chart'} describeChild>
                                <Button
                                    size="small"
                                    startIcon={asChart
                                        ? <TableRowsIcon fontSize="small" />
                                        : <BarChartIcon fontSize="small" />}
                                    onClick={(): void => setAsChart(!asChart)}
                                >
                                    {asChart ? 'Table' : 'Chart'}
                                </Button>
                            </Tooltip>
                        )}
                        {action}
                        <Tooltip
                            title={table.matched > table.rows.length
                                ? `Download these ${table.rows.length} rows`
                                : 'Download this table'}
                            describeChild
                        >
                            <Button size="small" startIcon={<DownloadIcon fontSize="small" />} onClick={download}>
                                CSV
                            </Button>
                        </Tooltip>
                    </Stack>
                )}

                {!table && action && (
                    <Box>{action}</Box>
                )}
            </Stack>
        </Paper>
    );
}

export default AgentInvestigation;
